import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import DialogMUI from '../../../shared/DialogMUI';
import ButtonMUI from '../../../shared/ButtonMUI';
import {closeModal} from '../../../redux/actions/app/appActions';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import InputMUI from '../../../shared/InputMUI';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import './CompaniesPopup.scss';
import {ReactComponent as PlusIcon} from '../../../assets/icons/plus-icon.svg';
import {ReactComponent as RemoveIcon} from '../../../assets/icons/remove.svg';
import {AutocompleteMUI} from '../../../shared';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {getEmployee} from '../../../redux/actions/employee/employeeActions';
import LoadPanel from '../../Components/LoadPanel';
import {notifyError, notifySuccess} from '../../../helpers/notifySnack';
import {getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import SelectComponent from '../../../shared/SelectComponent';
import {createCompany, getCompanies, getCompanyAvatars} from '../../../redux/actions/company/companyActions';
import DropzoneAvatar from '../../Components/DropzoneAvatar';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {mainPath} from '../../../routes/paths';

const CompaniesPopup = () => {
    const dispatch = useDispatch();
    const popupToShow = useSelector(({app}) => app.popupToShow);
    const {employee, employeeLoading} = useSelector(({employee}) => employee);
    const {countriesLoading, countries, cities, citiesLoading} = useSelector(({countries}) => countries);
    const {t} = useTranslation();
    const history = useHistory();
    const [filteredCities, setFilteredCities] = useState([]);
    const {avatars} = useSelector(({company}) => company);
    const schema = yup.object().shape({
        name: yup.string().required(t('CompaniesEditing.name_required')),
        countryId: yup.string().required(t('CompaniesEditing.country_required')),
        city: yup.string().required(t('CompaniesEditing.city_required')),
        address: yup.string().required(t('CompaniesEditing.address_required')),
        website: yup
            .string()
            .trim()
            .nullable()
            .notRequired()
            .test('is-url', t('validations.site'), (value) => {
                if (!value) return true;
                return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(value);
            }),
        email: yup.string().email(t('CompaniesEditing.email_invalid')).required(t('CompaniesEditing.email_required')),
        phones: yup.array().of(
            yup.object().shape({
                phone: yup.string().required(t('CompaniesEditing.phone_required'))
            })
        )
    });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: {errors, isValid, isSubmitting}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        shouldUnregister: false,
        defaultValues: {
            phones: [{phone: ''}],
            name: '',
            countryId: '',
            city: '',
            address: '',
            email: '',
            website: '',
            employee: [],
            logo: null
        }
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'phones' // Имя поля в форме
    });

    const logoWatch = watch('logo');

    const onCloseHandler = () => {
        dispatch(closeModal());
    };

    const onFileAdded = (file) => {
        setValue('logo', file);
    };

    const onSubmit = async (data) => {
        try {
            const formData = new FormData();

            formData.append('name', data.name);
            formData.append('countryId', data.countryId);
            formData.append('cityId', data.city);
            formData.append('address', data.address);
            formData.append('email', data.email);
            formData.append('site', data.website);

            if (data.logo && Object.keys(data.logo).length) {
                formData.append('logoId', data.logo);
            }

            data.phones.forEach((item, index) => {
                formData.append(`phones[${index}]`, item.phone);
            });

            data.employee.forEach((employee, index) => {
                formData.append(`employees[${index}]`, employee.id);
            });

            const res = await dispatch(createCompany(formData));

            if (res.payload && res.payload.status === 201) {
                notifySuccess(t('all.successfully'));
                await dispatch(getCompanies());
                history.push(mainPath.companies);
                onCloseHandler();
            }
        } catch (error) {
            // Handle any unexpected errors
            notifyError(t('all.error'));
            console.error('Unexpected error during form submission:', error);
        }
    };

    const popupVisible = popupToShow === POPUPS.COMPANY_POPUP;

    const employeeArr = employee
        .map(item => ({
            label: item.firstName ?? '',
            id: item.id,
            avatar: item?.avatar?.thumbnail ?? '',
            text: item.roleName ?? ''
        }));

    useEffect(() => {
        const fetchFunc = async () => {
            try {
                const employeeRes = await dispatch(getEmployee('active'));
                if (employeeRes.error) {
                    onCloseHandler();
                    return;
                }
                if (countriesLoading) {
                    const countriesRes = await dispatch(getCountries());
                    if (countriesRes.error) {
                        onCloseHandler();
                        return;
                    }
                }
                if(avatars.loading){
                    const avatarsRes = await dispatch(getCompanyAvatars());
                    if (avatarsRes.error) {
                        onCloseHandler();
                        return;
                    }
                }
                if (citiesLoading) {
                    const citiesRes = await dispatch(getCities());
                    if (citiesRes.error) {
                        onCloseHandler();
                    }
                }
            } catch (error) {
                notifyError(error.message || 'error');
            }
        };

        if (popupVisible) {
            fetchFunc();
        } else {
            reset();
        }
    }, [popupVisible]);

    const handleCountryChange = (selectedCountryId) => {
        const filtered = cities.filter((city) => city.countryId === selectedCountryId);
        setFilteredCities(filtered);
    };

    useEffect(() => {
        const countryId = watch('countryId');
        handleCountryChange(countryId);
    }, [cities, watch('countryId')]);

    const disabled = !logoWatch;

    return (
        <div className='companies__modal'>
            <DialogMUI open={popupVisible} onClose={onCloseHandler} title={t('CompaniesPopup.createCompany')}>
                {employeeLoading || countriesLoading || citiesLoading || avatars?.loading ? (
                    <LoadPanel staticPos={true} noBg={true}/>
                ) : (
                    <>
                        <div className='companies__dropzone'>
                            <DropzoneAvatar
                                maxFiles={1}
                                needLoader={false}
                                onFileAdded={onFileAdded}
                                images={avatars?.list || []}
                                path={POPUPS.AVATAR_EDIT_POPUP}
                            />
                        </div>
                        <div className='companies__input'>
                            <Controller
                                name='name'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='text'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.name?.message}
                                        inputProps={field}
                                        label={t('CompaniesPopup.companyName')}
                                    />
                                )}
                            />
                        </div>

                        <div className='companies__input'>
                            <Controller
                                name='countryId'
                                control={control}
                                render={({field}) => (
                                    <SelectComponent
                                        onChange={(e) => {
                                            field.onChange(e.target?.value ?? '');
                                            setValue('city', '', {shouldValidate: true});
                                        }}
                                        onBlur={field.onBlur}
                                        value={countries.find((c) => c.id === field.value)}
                                        options={countries}
                                        error={errors.countryId?.message}
                                        label={t('CompaniesPopup.country')}
                                        placeholder=''
                                        inputProps={field}
                                    />
                                )}
                            />
                        </div>
                        <div className='companies__input'>
                            <Controller
                                name='city'
                                control={control}
                                render={({field}) => (
                                    <SelectComponent
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={filteredCities.find((c) => c.id === field.value)}
                                        options={filteredCities}
                                        error={errors.city?.message}
                                        label={t('CompaniesPopup.city')}
                                        placeholder=''
                                        inputProps={field}
                                        disabled={!filteredCities.length}
                                    />
                                )}
                            />
                        </div>
                        <div className='companies__input'>
                            <Controller
                                name='address'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='text'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.address?.message}
                                        inputProps={field}
                                        label={t('CompaniesPopup.address')}
                                    />
                                )}
                            />
                        </div>
                        <div className='companies__input'>
                            <Controller
                                name='email'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='email'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.email?.message}
                                        inputProps={field}
                                        label={t('CompaniesPopup.email')}
                                    />
                                )}
                            />
                        </div>

                        {fields.map((field, index) => (
                            <div key={field.id} className='companies__input'>
                                <Controller
                                    name={`phones[${index}].phone`}
                                    control={control}
                                    render={({field}) => (
                                        <div className={'companies__input-inner'}>
                                            <InputMUI
                                                type='tel'
                                                fullWidth
                                                className={`no-icons ${index > 0 ? 'with-clear' : ''}`}
                                                error={errors.phones?.[index]?.phone?.message}
                                                label={`${
                                                    index > 0 ? t('all.additional') + ' ' + t('CompaniesPopup.phoneL') : t('CompaniesPopup.phone')
                                                }`}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                                                    if (cleanValue.charAt(0) !== '+') {
                                                        cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                                                    } else {
                                                        cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                                                    }

                                                    if (/^\+?\d*$/.test(cleanValue)) {
                                                        field.onChange(cleanValue);
                                                    }
                                                }}
                                                value={field.value}
                                                inputProps={field}
                                            />

                                            {index > 0 && (
                                                <div className='companies__trash' onClick={() => remove(index)}>
                                                    {<RemoveIcon/>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                        ))}

                        <div className='companies__dynamic' onClick={() => append({phone: ''})}>
                            <PlusIcon/>
                            <span>{t('CompaniesPopup.addPhone')}</span>
                        </div>
                        <div className='companies__input'>
                            <Controller
                                name='website'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='text'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.website?.message}
                                        inputProps={field}
                                        label={t('CompaniesPopup.website')}
                                    />
                                )}
                            />
                        </div>
                        <div className='companies__select'>
                            <p>{t('CompaniesPopup.addEmployee')}</p>
                            <Controller
                                name='employee'
                                control={control}
                                render={({field}) => (
                                    <AutocompleteMUI
                                        className='no-icons'
                                        text={t('CompaniesPopup.selectFromList')}
                                        array={employeeArr}
                                        value={field.value}
                                        error={errors?.employee}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                        <div className='companies__submit'>
                            <ButtonMUI
                                disabled={!isValid || disabled || isSubmitting || !logoWatch}
                                loading={isSubmitting}
                                onClick={handleSubmit(onSubmit)}
                                formAction
                                fullWidth={true}
                            >
                                {t('CompaniesPopup.createCompany')}
                            </ButtonMUI>
                        </div>
                    </>
                )}
            </DialogMUI>
        </div>
    );
};
export default CompaniesPopup;
